import { pullSessionFromUrl } from '@/data/session-data';
import { isInviteRole } from '@/data/roles';
import { redirect } from '@/lib/redirect';
import { useAccountsStore } from '@/stores/accounts';
import { useUserStore } from '@/stores/user';
import AccountView from '@/views/account/AccountView.vue';
import CheckoutView from '@/views/account/billing/CheckoutView.vue';
import ExitSurveyView from '@/views/account/billing/ExitSurveyView.vue';
import BillingPortalView from '@/views/account/billing/portal/BillingPortalView.vue';
import PasswordView from '@/views/account/PasswordView.vue';
import ProfileView from '@/views/account/ProfileView.vue';
import ReferralView from '@/views/account/ReferralView.vue';
import LoginView from '@/views/auth/LoginView.vue';
import LostPasswordView from '@/views/auth/LostPasswordView.vue';
import ReferralApplyView from '@/views/auth/ReferralApplyView.vue';
import SignupView from '@/views/auth/SignupView.vue';
import NotFoundView from '@/views/NotFoundView.vue';
import OnboardingView from '@/views/onboarding/OnboardingView.vue';
import { createRouter, createWebHistory } from 'vue-router';

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      // `/` is never rendered — `beforeEach` always redirects to
      // `/billing/portal` (authed) or `/auth/login` (anon). vue-router
      // requires a component, so use an empty inline render fn.
      path: '/',
      component: { render: () => null },
    },
    {
      // `/auth` (and `/auth/`) is never rendered — `beforeEach` redirects
      // to `/auth/login` (returning user) or `/auth/signup` (first-timer)
      // based on the `hasLoggedIn` flag, or sends authed users back to
      // wherever they came from.
      path: '/auth',
      component: { render: () => null },
      meta: { public: true },
    },
    {
      path: '/auth/signup',
      name: 'signup',
      component: SignupView,
      meta: { public: true },
    },
    {
      path: '/auth/login',
      name: 'login',
      component: LoginView,
      meta: { public: true },
    },
    {
      // Authed users hitting `/auth?referralCode=...` are routed here so
      // the code can be applied to their existing subscription via a modal.
      // Anonymous users still flow through SignupView as before.
      path: '/auth/referral',
      name: 'referral-apply',
      component: ReferralApplyView,
      meta: { requiresAuth: true },
    },
    {
      path: '/account/lost-password',
      name: 'lost-password',
      component: LostPasswordView,
      meta: { public: true },
    },
    {
      path: '/account/onboarding',
      name: 'onboarding',
      component: OnboardingView,
    },
    {
      path: '/account',
      component: AccountView,
      meta: { requiresAuth: true },
      children: [
        {
          path: 'password',
          name: 'password',
          component: PasswordView,
        },
        {
          path: 'profile',
          name: 'profile',
          component: ProfileView,
        },
        {
          path: 'settings',
          redirect: { name: 'profile' },
        },
        {
          path: 'referral',
          name: 'referral',
          component: ReferralView,
        },
      ],
    },
    {
      path: '/billing',
      meta: { requiresAuth: true },
      component: AccountView,
      children: [
        {
          path: 'portal',
          name: 'portal',
          component: BillingPortalView,
        },
        {
          // `/billing/plans` has no dedicated view; it forwards to the
          // portal and opens the change-plan modal there (the
          // `changePlan` query is watched in BillingPortalView.vue).
          // Preserved so old links / bookmarks keep working.
          path: 'plans',
          redirect: { name: 'portal', query: { changePlan: '1' } },
        },
        {
          path: 'checkout',
          name: 'billing-checkout',
          component: CheckoutView,
        },
        {
          path: 'exit-survey',
          name: 'exit-survey',
          component: ExitSurveyView,
        },
      ],
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: NotFoundView,
    },
  ],
});

router.beforeEach(async (to, from) => {
  if (typeof window !== 'undefined') {
    pullSessionFromUrl(new URL(window.location.href));
  }

  // Wait for auth resolution before deciding redirects. init() is
  // idempotent and cached, so this resolves immediately on every
  // navigation after the first.
  await useAccountsStore().init();

  const { uid, email } = useUserStore().user;
  const isAuthed = Boolean(uid && email);

  if (to.path === '/') {
    return isAuthed ? { path: '/billing/portal', replace: true } : { path: '/auth/login', replace: true };
  }

  if (to.path === '/auth' || to.path === '/auth/') {
    if (isAuthed) {
      // pullSessionFromUrl above has already moved any ?referralCode= into
      // sessionStorage. If one is pending, divert to the apply-modal view
      // before the normal post-auth redirect.
      if (sessionStorage.getItem('referralCode')) {
        return { path: '/auth/referral', replace: true };
      }
      try {
        await redirect();
        return false;
      } catch {
        return { path: '/billing/portal', replace: true };
      }
    }
    const hasLoggedIn = sessionStorage.getItem('hasLoggedIn') === '1' || localStorage.getItem('hasLoggedIn') === '1';
    return { path: hasLoggedIn ? '/auth/login' : '/auth/signup', replace: true };
  }

  // Invited collaborators (beta reader / editor) are almost always new users.
  // On their *initial* landing on the login form (the cross-origin bounce from
  // the app), forward first-time visitors to signup instead — mirrors the
  // `/auth` dispatcher's first-timer branch above. `from.matched.length === 0`
  // scopes this to that first navigation only, so an explicit in-app "Login"
  // click from the signup page is never trapped. Returning users (hasLoggedIn)
  // keep the login form. `role` is already in sessionStorage by now
  // (pullSessionFromUrl ran at the top).
  if (to.name === 'login' && !isAuthed && from.matched.length === 0) {
    const hasLoggedIn = sessionStorage.getItem('hasLoggedIn') === '1' || localStorage.getItem('hasLoggedIn') === '1';
    if (!hasLoggedIn && isInviteRole(sessionStorage.getItem('role'))) {
      return { path: '/auth/signup', replace: true };
    }
  }

  // Auth pages (login, signup) are always reachable, even when a user is
  // already signed in — Switch Account → "Add another account" depends on it.
  const isPublic = to.matched.some((record) => record.meta.public);
  if (isPublic || isAuthed) {
    return true;
  }

  sessionStorage.setItem('redirect', `${location.origin}${to.fullPath}`);
  return { path: '/auth/login', replace: true };
});

export default router;
